import moment from 'moment-timezone';

/** Default matches backend scheduler.timezone when unset. */
export const DEFAULT_SCHEDULER_TIMEZONE = 'Asia/Kolkata';

/**
 * Current GMT/UTC offset for an IANA zone, e.g. "GMT+5:30", "GMT-5", "GMT+0".
 * Uses today's rules (includes DST where applicable).
 * @param {string} iana
 * @returns {string}
 */
export function formatGmtOffset(iana) {
  if (!iana || String(iana).trim() === '') return 'GMT';
  const zone = String(iana).trim();
  if (zone === 'UTC') return 'GMT+0';
  try {
    if (!moment.tz.zone(zone)) return 'GMT';
    const offsetMin = moment.tz(zone).utcOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    if (minutes === 0) {
      return `GMT${sign}${hours}`;
    }
    return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  } catch (_) {
    return 'GMT';
  }
}

/** @param {{ value: string, label: string }} opt */
export function formatTimezoneOptionLabel(opt) {
  const gmt = formatGmtOffset(opt.value);
  return `${opt.label} (${gmt})`;
}

/**
 * IANA time zones for Scheduled Jobs (grouped picklist).
 * value = IANA id passed to API / stored on scheduler.timezone
 */
export const SCHEDULER_TIMEZONE_GROUPS = [
  {
    label: 'UTC',
    options: [{ value: 'UTC', label: 'UTC (Coordinated Universal Time)' }],
  },
  {
    label: 'Gulf & Middle East',
    options: [
      { value: 'Asia/Dubai', label: 'Gulf — Dubai (UAE)' },
      { value: 'Asia/Muscat', label: 'Gulf — Muscat (Oman)' },
      { value: 'Asia/Riyadh', label: 'Gulf — Riyadh (Saudi Arabia)' },
      { value: 'Asia/Kuwait', label: 'Gulf — Kuwait' },
      { value: 'Asia/Qatar', label: 'Gulf — Qatar' },
      { value: 'Asia/Bahrain', label: 'Gulf — Bahrain' },
      { value: 'Asia/Tehran', label: 'Iran — Tehran' },
    ],
  },
  {
    label: 'South Asia',
    options: [
      { value: 'Asia/Kolkata', label: 'India — Kolkata' },
      { value: 'Asia/Karachi', label: 'Pakistan — Karachi' },
      { value: 'Asia/Dhaka', label: 'Bangladesh — Dhaka' },
      { value: 'Asia/Colombo', label: 'Sri Lanka — Colombo' },
    ],
  },
  {
    label: 'Singapore & East Asia',
    options: [
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Shanghai', label: 'China — Shanghai' },
      { value: 'Asia/Tokyo', label: 'Japan — Tokyo' },
      { value: 'Asia/Seoul', label: 'South Korea — Seoul' },
    ],
  },
  {
    label: 'Australia & New Zealand',
    options: [
      { value: 'Australia/Sydney', label: 'Australia — Sydney (AEST/AEDT)' },
      { value: 'Australia/Melbourne', label: 'Australia — Melbourne' },
      { value: 'Australia/Brisbane', label: 'Australia — Brisbane' },
      { value: 'Australia/Perth', label: 'Australia — Perth' },
      { value: 'Australia/Adelaide', label: 'Australia — Adelaide' },
      { value: 'Pacific/Auckland', label: 'New Zealand — Auckland' },
    ],
  },
  {
    label: 'United Kingdom & Europe',
    options: [
      { value: 'Europe/London', label: 'United Kingdom — London' },
      { value: 'Europe/Dublin', label: 'Ireland — Dublin' },
      { value: 'Europe/Paris', label: 'Europe — Paris (CET/CEST)' },
      { value: 'Europe/Berlin', label: 'Europe — Berlin' },
      { value: 'Europe/Amsterdam', label: 'Europe — Amsterdam' },
      { value: 'Europe/Zurich', label: 'Europe — Zurich' },
    ],
  },
  {
    label: 'United States & Canada',
    options: [
      { value: 'America/New_York', label: 'US — Eastern (New York)' },
      { value: 'America/Chicago', label: 'US — Central (Chicago)' },
      { value: 'America/Denver', label: 'US — Mountain (Denver)' },
      { value: 'America/Los_Angeles', label: 'US — Pacific (Los Angeles)' },
      { value: 'America/Phoenix', label: 'US — Arizona (no DST)' },
      { value: 'America/Anchorage', label: 'US — Alaska' },
      { value: 'Pacific/Honolulu', label: 'US — Hawaii' },
      { value: 'America/Toronto', label: 'Canada — Toronto' },
      { value: 'America/Vancouver', label: 'Canada — Vancouver' },
    ],
  },
];

const KNOWN_VALUES = new Set(
  SCHEDULER_TIMEZONE_GROUPS.flatMap((g) => g.options.map((o) => o.value))
);

/** Flat list for validation / lookup */
export function isKnownSchedulerTimezone(value) {
  return value != null && KNOWN_VALUES.has(String(value));
}

/**
 * Resolve stored timezone for select; keeps unknown legacy values visible in edit.
 * @param {string} [stored]
 * @returns {string}
 */
export function resolveSchedulerTimezoneSelectValue(stored) {
  const t = stored != null && String(stored).trim() !== '' ? String(stored).trim() : '';
  if (!t) return DEFAULT_SCHEDULER_TIMEZONE;
  return t;
}

/** Extra option when editing a row whose timezone is not in the catalog */
export function extraTimezoneOptionIfNeeded(stored) {
  const t = resolveSchedulerTimezoneSelectValue(stored);
  if (KNOWN_VALUES.has(t)) return null;
  return { value: t, label: `${t} (saved)` };
}
